import React from "react";
import { Input } from "antd";
import "./ReturnPricingFields.scss";

/**
 * Return distance & time, for the client and driver rate cards together. This is bracket
 * billing (mirrors `resolveReturnDistanceTier` in helpers.ts / helper/returnPricing.js):
 * whichever band the total return distance lands in, the ENTIRE distance bills at that
 * band's per-mile rate. There is no separately-billed band between Short and Long.
 *
 *   0 < miles <= Free threshold              -> not billed
 *   Free threshold < miles <= Long threshold  -> Short rate, whole distance
 *   miles > Long threshold                    -> Long rate, whole distance
 *
 * Laid out as 3 tier columns (Free / Short / Long), each split into Client (left) and
 * Driver (right) sub-columns -- 6 columns total -- with a Distance field on top and a Rate
 * field below in each. The Short tier's Distance field is never directly edited: it's just
 * the range between the Free and Long thresholds entered in the columns either side of it.
 */
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const onChangeFor = (data, setData) => (e) => {
  const { name, value } = e.target;
  setData({ ...data, [name]: value });
};

const ReturnPricingFields = ({
  clientData,
  setClientData,
  driverData,
  setDriverData
}) => {
  const clientChange = onChangeFor(clientData, setClientData);
  const driverChange = onChangeFor(driverData, setDriverData);

  const shortRangeLabel = (data) => {
    const freeMax = num(data.maxReturnDistanceShort);
    const longMax = num(data.maxReturnDistanceLong);
    if (freeMax !== null && longMax !== null) return `${freeMax} – ${longMax} mi`;
    if (freeMax !== null) return `over ${freeMax} mi`;
    return "—";
  };

  return (
    <div className="return-pricing-box additionalBorder">
      <div className="return-pricing-title">
        Return distance &amp; time
        <span className="return-pricing-sub">last stop back to the pickup / HQ</span>
      </div>
      <div className="return-pricing-help">
        Billed only when an order&apos;s Return Process has &ldquo;Return to pickup after
        all stops&rdquo; on. This is bracket billing: whichever band the total return
        distance falls in, the ENTIRE return distance bills at that band&apos;s rate
        &mdash; there is no separate charge for &ldquo;the miles between&rdquo;. Leave the
        Long distance blank to skip that tier (everything past the Free threshold then
        bills at the Short rate). To bill a single flat rate per mile with no free miles,
        set both distances to 0 and both rates to the same value. Return time is billed on
        top, whatever the tier.
      </div>

      <div className="return-pricing-grid">
        <div className="return-pricing-tier-header">Free</div>
        <div className="return-pricing-tier-header">Short</div>
        <div className="return-pricing-tier-header">Long</div>

        <div className="return-pricing-subheader">Client</div>
        <div className="return-pricing-subheader">Driver</div>
        <div className="return-pricing-subheader">Client</div>
        <div className="return-pricing-subheader">Driver</div>
        <div className="return-pricing-subheader">Client</div>
        <div className="return-pricing-subheader">Driver</div>

        <div className="return-pricing-field-label">Distance</div>
        <div className="return-pricing-field-label">Distance</div>
        <div className="return-pricing-field-label">Distance</div>
        <div className="return-pricing-field-label">Distance</div>
        <div className="return-pricing-field-label">Distance</div>
        <div className="return-pricing-field-label">Distance</div>

        {/* Free tier: distance = editable Free/Short threshold, rate is always $0 */}
        <Input
          name="maxReturnDistanceShort"
          value={clientData.maxReturnDistanceShort}
          onChange={clientChange}
          type="number"
          addonAfter="mi"
          placeholder="0"
        />
        <Input
          name="maxReturnDistanceShort"
          value={driverData.maxReturnDistanceShort}
          onChange={driverChange}
          type="number"
          addonAfter="mi"
          placeholder="0"
        />

        {/* Short tier: distance is computed (range between Free and Long thresholds) */}
        <div className="return-pricing-range-display">{shortRangeLabel(clientData)}</div>
        <div className="return-pricing-range-display">{shortRangeLabel(driverData)}</div>

        {/* Long tier: distance = editable Long threshold */}
        <Input
          name="maxReturnDistanceLong"
          value={clientData.maxReturnDistanceLong}
          onChange={clientChange}
          type="number"
          addonAfter="mi"
          placeholder="unbounded"
        />
        <Input
          name="maxReturnDistanceLong"
          value={driverData.maxReturnDistanceLong}
          onChange={driverChange}
          type="number"
          addonAfter="mi"
          placeholder="unbounded"
        />

        <div className="return-pricing-field-label">Rate</div>
        <div className="return-pricing-field-label">Rate</div>
        <div className="return-pricing-field-label">Rate</div>
        <div className="return-pricing-field-label">Rate</div>
        <div className="return-pricing-field-label">Rate</div>
        <div className="return-pricing-field-label">Rate</div>

        <Input value="Free" disabled />
        <Input value="Free" disabled />

        <Input
          name="pricePerReturnDistanceShort"
          value={clientData.pricePerReturnDistanceShort}
          onChange={clientChange}
          type="number"
          addonBefore={`${window.CURRENCY_SYMBOL}/mi`}
          placeholder="0.00"
        />
        <Input
          name="pricePerReturnDistanceShort"
          value={driverData.pricePerReturnDistanceShort}
          onChange={driverChange}
          type="number"
          addonBefore={`${window.CURRENCY_SYMBOL}/mi`}
          placeholder="0.00"
        />

        <Input
          name="pricePerReturnDistanceLong"
          value={clientData.pricePerReturnDistanceLong}
          onChange={clientChange}
          type="number"
          addonBefore={`${window.CURRENCY_SYMBOL}/mi`}
          placeholder="0.00"
        />
        <Input
          name="pricePerReturnDistanceLong"
          value={driverData.pricePerReturnDistanceLong}
          onChange={driverChange}
          type="number"
          addonBefore={`${window.CURRENCY_SYMBOL}/mi`}
          placeholder="0.00"
        />
      </div>

      <div className="return-pricing-time-row">
        <div className="return-pricing-time-card">
          <span>Return time, any tier (Client)</span>
          <Input
            name="pricePerReturnTime"
            value={clientData.pricePerReturnTime}
            onChange={clientChange}
            type="number"
            addonBefore={`${window.CURRENCY_SYMBOL}/hr`}
            placeholder="0.00"
          />
        </div>
        <div className="return-pricing-time-card">
          <span>Return time, any tier (Driver)</span>
          <Input
            name="pricePerReturnTime"
            value={driverData.pricePerReturnTime}
            onChange={driverChange}
            type="number"
            addonBefore={`${window.CURRENCY_SYMBOL}/hr`}
            placeholder="0.00"
          />
        </div>
      </div>
    </div>
  );
};

export default ReturnPricingFields;
